/**
 * "Choose an option" prompt buttons.
 *
 * Product pages open with no variant selected, so the real buy buttons start
 * disabled. The prompt button rendered by `inflyn-choose-option-button` stands
 * in for them until every option is answered, and a click scrolls to the first
 * option still unanswered, pulses it and moves focus there.
 *
 * The picker's own data-selection-complete flag is only maintained for
 * style + size products, so this module keeps its own flag on the section
 * (data-inflyn-selection-pending) and the snippet's CSS keys off that.
 */

const PROMPT_SELECTOR = '[data-inflyn-choose-option]';
const PICKER_SELECTOR = 'variant-picker[data-template-product-match="true"]';
const TARGET_CLASS = 'inflyn-choose-option-target';

/** @param {Element} element */
function findPicker(element) {
  return element.closest('.shopify-section')?.querySelector(PICKER_SELECTOR) ?? null;
}

/**
 * First fieldset / select the shopper still has to answer, in on-screen order.
 * Mirrors VariantPicker#isSelectionComplete.
 * @param {Element} picker
 * @returns {HTMLElement | null}
 */
function findUnansweredOption(picker) {
  for (const option of picker.querySelectorAll('fieldset, select')) {
    if (!(option instanceof HTMLElement)) continue;
    if (option instanceof HTMLSelectElement ? option.value === '' : !option.querySelector('input:checked')) {
      return option;
    }
  }
  return null;
}

/** @param {Element} picker */
function syncState(picker) {
  const section = picker.closest('.shopify-section');
  if (!(section instanceof HTMLElement)) return;

  section.dataset.inflynSelectionPending = String(Boolean(findUnansweredOption(picker)));
}

/** @param {MouseEvent} event */
function handlePromptClick(event) {
  const prompt = event.target instanceof Element ? event.target.closest(PROMPT_SELECTOR) : null;
  if (!prompt) return;

  event.preventDefault();
  const picker = findPicker(prompt);
  if (!picker) return;

  const option = findUnansweredOption(picker);
  if (!option) return;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  option.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });

  option.classList.remove(TARGET_CLASS);
  // Restart the pulse on repeated clicks.
  void option.offsetWidth;
  option.classList.add(TARGET_CLASS);
  option.addEventListener('animationend', () => option.classList.remove(TARGET_CLASS), { once: true });

  const focusTarget =
    option instanceof HTMLSelectElement
      ? option
      : option.querySelector('input:not([aria-disabled="true"])') ?? option.querySelector('input');
  if (focusTarget instanceof HTMLElement) focusTarget.focus({ preventScroll: true });
}

/** @param {Element} picker */
function watchPicker(picker) {
  let timer = 0;
  // Let the picker settle first: it clears dependent options on change, and
  // morphs itself from the server response after a selection.
  const scheduleSync = () => {
    clearTimeout(timer);
    timer = setTimeout(() => syncState(picker));
  };

  picker.addEventListener('change', scheduleSync);
  new MutationObserver(scheduleSync).observe(picker, { subtree: true, childList: true, attributes: true });
  syncState(picker);
}

if (!window.inflynChooseOptionReady) {
  window.inflynChooseOptionReady = true;
  document.addEventListener('click', handlePromptClick);
  for (const picker of document.querySelectorAll(PICKER_SELECTOR)) watchPicker(picker);
}
